import type { BandReason, ScoreStep } from './api'

// The band in one line, on the screens (release 3.3, N2 of the 12 Oct
// research).
//
// # WHY THE CONSOLE DOES NOT WRITE THE LINE ITSELF
//
// The line is the step of the stored explanation that decided the band, found
// by replaying the engine's stages with the model the score was taken under.
// That is the API's to do (packages/core/scoring/bandline.go), once, so the
// register, the record, the export and an approval all say the same words. The
// console only shortens it where a band chip already says the band, and finds
// the same step in "How the band was reached" to mark it.

// The line without its leading band word, for a place where the band chip is
// already beside it: "High: Data at rest is not encrypted (ENC-01)." becomes
// "Data at rest is not encrypted (ENC-01)."
export function reasonWords(r: BandReason | undefined | null): string {
  // The API's own field: the step's words alone. Sliced from the line, an
  // override clause on a line with no step ("High. Set by hand to Medium.")
  // would sit beside the High chip as if it were the reason for High (review
  // of 12 Oct, N4).
  if (typeof r?.reason === 'string') return r.reason
  if (!r?.line) return ''
  const i = r.line.indexOf(': ')
  // A line that is only the band ("High.") says nothing beside a chip.
  if (i < 0) return ''
  return r.line.slice(i + 2)
}

// The step of a shown explanation that decided the band. The explanation on
// screen may leave steps out (the inputs list), so the step is matched by what
// it says rather than by where it stood in the stored list.
export function decidingStep(shown: ScoreStep[], stored: ScoreStep[] | undefined, r: BandReason | undefined | null): number {
  if (!r || r.stepIndex < 0 || !stored) return -1
  const decided = stored[r.stepIndex]
  if (!decided) return -1
  return shown.findIndex((s) => s.step === decided.step && (s.gateKey ?? '') === (decided.gateKey ?? '') && s.detail === decided.detail)
}

// A step of an explanation on screen, and whether it decided the band.
export type MarkedStep = ScoreStep & { decided?: boolean }

// The steps as shown, with the deciding one marked.
export function markDeciding(shown: ScoreStep[], stored: ScoreStep[] | undefined, r: BandReason | undefined | null): MarkedStep[] {
  const at = decidingStep(shown, stored, r)
  return shown.map((s, i) => (i === at ? { ...s, decided: true } : s))
}
